
// Added React import to provide namespace for Dispatch and SetStateAction types
import React, { useState, useCallback, useRef } from 'react';
import { CampingSpot, Coordinates } from '../types';
import { fetchNearbyHikingTrails } from '../services/hikingService';
import { analyzeSpot, generateSpotName, generateSpotShareSummary } from '../services/geminiService';
import { upsertSpotsToFirebase, markSpotAsInvalid } from '../services/firebaseService';
import { shouldAutoGenerateSpotDescription } from '../services/spotIdentity';
import { fetchNearestBuildingDist, findNearestRoadForPoint } from '../services/overpassService';
import { getNearestCharger } from '../services/nobilService';
import { fetchWikiContext } from '../services/wikiService';
import { deriveRoadAccessClass } from '../services/roadAccessClassifier';
import { fetchNearbyForestRoad } from '../services/nvdbService';
import { getDistance } from '../services/geometryUtils';
import { BRAND } from '../constants/mapColors';

// Max zoom for gratis-brukere — speiles fra App.tsx FREE_ZOOM_MAX.
// Programmatic spot-select skal ikke kunne zoome forbi denne for ikke-betalte.
const FREE_ZOOM_MAX = 13;

export const useSelectionManager = (
  spots: CampingSpot[],
  setSpots: React.Dispatch<React.SetStateAction<CampingSpot[]>>,
  setMapCenter: (c: Coordinates) => void,
  setMapZoom: (z: number) => void,
  setLayers: React.Dispatch<React.SetStateAction<any>>,
  setActiveAnalysisIds: (fn: (prev: Set<string>) => Set<string>) => void,
  setMapCommand?: (cmd: { center: Coordinates; zoom: number; offsetY?: number; id: number } | null) => void,
  addToast?: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void,
  isPaidUser: boolean = true
) => {
  const [selectedSpotId, setSelectedSpotId] = useState<string | null>(null);
  const [activeHikingTrailId, setActiveHikingTrailId] = useState<string | null>(null);
  const [showMobileSheet, setShowMobileSheet] = useState(false);
  const [generatingMetaIds, setGeneratingMetaIds] = useState<Set<string>>(new Set());
  const generatingMetaIdsRef = useRef<Set<string>>(new Set());

  // State for visual feedback when a spot is removed
  const [removalFeedback, setRemovalFeedback] = useState<{ name: string; reason: string } | null>(null);

  const reanalyzeSpot = useCallback(async (s: CampingSpot): Promise<CampingSpot | null> => {
    setActiveAnalysisIds(prev => new Set(prev).add(s.id));
    try {
      // 1. Hent nøyaktig husavstand OG vei-info på nytt
      const [currentHouseDist, roadInfo] = await Promise.all([
        fetchNearestBuildingDist(s.location),
        findNearestRoadForPoint(s.location)
      ]);

	      const fallbackForestRoad =
	        (!roadInfo || roadInfo.distance > 100 || !roadInfo.isDriveable)
	          ? await fetchNearbyForestRoad(s.location)
	          : null;
	      const fallbackRoadCategory = fallbackForestRoad?.vegkategori || '';
	      const fallbackHighway =
	        fallbackRoadCategory === 'S'
	          ? 'unclassified'
	          : fallbackRoadCategory === 'P'
	            ? 'service'
	            : null;
	      const fallbackRoadDistance = fallbackForestRoad?.roadGeometry?.reduce((minDist, point) => {
	        const d = getDistance(s.location.latitude, s.location.longitude, point.latitude, point.longitude);
	        return d < minDist ? d : minDist;
	      }, Number.POSITIVE_INFINITY);
	      const distToRoad = Math.min(
	        roadInfo?.distance ?? 999,
	        Number.isFinite(fallbackRoadDistance) ? Number(fallbackRoadDistance) : 999,
	      );
	      const updatedRoadContext = {
	        ...s,
	        nearestHouse: currentHouseDist,
	        highway: roadInfo?.highway || fallbackHighway || s.highway,
	        surfaceType: roadInfo?.surface || s.surfaceType,
	        isDriveable: roadInfo?.isDriveable ?? !!fallbackHighway,
	        nearestDriveableHighway: roadInfo?.nearestDriveableHighway || fallbackHighway,
	        nearestDriveableDist: roadInfo?.nearestDriveableDist || (Number.isFinite(fallbackRoadDistance) ? Math.round(Number(fallbackRoadDistance)) : null),
	        accessRoadType:
	          fallbackRoadCategory === 'S'
	            ? 'skogsbilveg'
	            : fallbackRoadCategory === 'P'
	              ? 'serviceveg'
	              : s.accessRoadType,
	        roadCategory: fallbackRoadCategory || (s as any).roadCategory,
	      };
      const updatedWithDist = {
        ...updatedRoadContext,
        ...deriveRoadAccessClass(updatedRoadContext),
      };

      // 2. Kjør AI-analyse med oppdaterte data
      const ai = await analyzeSpot(updatedWithDist, true);

      if (ai) {
	        const isFacility = s.features.some(f => ["GAPAHUK", "BÅLPLASS", "PICNIC", "CAMPINGPLASS", "KAI/BRYGGE"].includes(f.toUpperCase()));
	        const roadDistanceLimit = fallbackForestRoad ? 350 : 100;
	        const isNoRoad = distToRoad > roadDistanceLimit && !isFacility;

        // Krav: Score under 4, forbudt, for nær hus, eller ingen vei -> Fjern
        const isBad = ai.score < 4 || ai.anbefaling === 'FORBUDT' || currentHouseDist < 150 || isNoRoad;

        if (isBad) {
          let reason = "Lav score: Ikke egnet for camping";
          if (currentHouseDist < 150) reason = `For nær bebyggelse (${currentHouseDist}m)`;
          else if (isNoRoad) reason = "Ingen kjørbar vei funnet";
          else if (ai.anbefaling === 'FORBUDT') reason = "Vurdert som forbudt område";
          else if (ai.score < 4) reason = `Lav kvalitetsscore (${ai.score}/10)`;

          // 1. Marker som ugyldig i databasen
          await markSpotAsInvalid(s.id, `Automatisk vask: ${reason}`);

          // 2. Fjern fra kartet umiddelbart
          setSpots(prev => prev.filter(x => x.id !== s.id));

          // 3. Lukk visning hvis den er valgt
          if (selectedSpotId === s.id) {
            setSelectedSpotId(null);
            setShowMobileSheet(false);
          }

          // 4. Gi visuell beskjed til brukeren
          setRemovalFeedback({ name: s.name, reason });
          addToast?.(`Uegnet: ${reason}`, 'warning');
          return null;

        } else {
          // Godkjent - Oppdater punktet
          const updated = {
            ...updatedWithDist,
            aiResult: ai,
            score: ai.score,
            description: ai.vurdering || s.description,
            isInvalid: false,
            suitabilityColor: ai.score >= 8 ? BRAND : ai.score >= 4 ? '#f59e0b' : '#78716c'
          };
          setSpots(prev => prev.map(x => x.id === s.id ? updated : x));
          await upsertSpotsToFirebase([updated]);
          addToast?.("Egnet for camping!", 'success');
          return updated;
        }
      }
      return s;
    } catch (err) {
      console.error("Reanalyse feilet:", err);
      return s;
    } finally {
      setActiveAnalysisIds(prev => {
        const next = new Set(prev);
        next.delete(s.id);
        return next;
      });
    }
  }, [setSpots, setActiveAnalysisIds, selectedSpotId]);

  const handleSelectSpot = useCallback(async (s: CampingSpot) => {
    if (!s || !s.location) return;
    setSelectedSpotId(s.id);
    setActiveHikingTrailId(null);
    setMapCenter(s.location);
    // Gratisbrukere skal ikke kunne zoome forbi FREE_ZOOM_MAX via spot-klikk.
    const targetZoom = isPaidUser ? 17 : Math.min(17, FREE_ZOOM_MAX);
    setMapZoom(targetZoom);

    if (setMapCommand) {
      setMapCommand({ center: s.location, zoom: targetZoom, id: performance.now() });
    }

    // Auto-aktivér satellitt-lag kun for betalte (satellite_map er premium-feature).
    if (isPaidUser) {
      setLayers((prev: any) => ({ ...prev, satellite: true }));
    }

    // FETCH WIKI CONTEXT FOR DEBUG CONSOLE
    fetchWikiContext(s.location);

    // Mobile UX: open selected card in list panel, not as overlapping bottom sheet.
    if (window.innerWidth < 768) {
      setShowMobileSheet(false);
    }

    if (!s.hikingTrails) {
      fetchNearbyHikingTrails(s.location).then(trails => {
        if (trails?.length > 0) {
          setSpots(prev => prev.map(x => x.id === s.id ? { ...x, hikingTrails: trails } : x));
        }
      });
    }

    if (!s.nearestCharger) {
      getNearestCharger(s.location, 10000).then(charger => {
        if (charger) {
          setSpots(prev => prev.map(x => x.id === s.id ? { ...x, nearestCharger: charger } : x));
        }
      });
    }

    const latestSpot =
      !s.aiResult && s.spotType === 'algorithmic'
        ? (await reanalyzeSpot(s)) || null
        : s;

    if (!latestSpot) return;

    // Generate and persist missing title/description in the background.
    if (!generatingMetaIdsRef.current.has(latestSpot.id)) {
      generatingMetaIdsRef.current.add(latestSpot.id);
      const currentSpot = latestSpot;

      const missingTitle =
        !currentSpot.name ||
        /^(lokasjon|punkt|unnamed|unavngitt)$/i.test(currentSpot.name.trim());
      const currentDescription = currentSpot.aiResult?.description || currentSpot.description || '';
      const missingDescription =
        !currentDescription.trim() ||
        /analyserer|analyzing|waiting for analysis|venter pa analyse|punkt funnet i kartdata|dette er en vill campingplass identifisert|beskrivelse kommer/i.test(currentDescription);

      // Informasjons-/faremarkører (bom, forbudt-sone) og attraksjons-POI-er skal
      // IKKE få auto-generert fricamp-villmarksbeskrivelse — den blir feilaktig
      // (en bom er ikke et sted å campe). For slike vises kun brukerens egen tekst.
      if ((missingTitle || missingDescription) && shouldAutoGenerateSpotDescription(currentSpot)) {
        setGeneratingMetaIds(prev => new Set(prev).add(currentSpot.id));
        (async () => {
          try {
            const [title, summary] = await Promise.all([
              missingTitle
                ? generateSpotName({
                  highway: currentSpot.highway,
                  surfaceType: currentSpot.surfaceType,
                  features: currentSpot.features
                })
                : Promise.resolve(null),
              missingDescription
                ? generateSpotShareSummary({
                  spotName: currentSpot.name,
                  highway: currentSpot.highway,
                  surfaceType: currentSpot.surfaceType,
                  nearestHouse: currentSpot.nearestHouse,
                  score: currentSpot.score,
                  features: currentSpot.features,
                  municipality: currentSpot.property?.kommunenavn
                })
                : Promise.resolve(null)
            ]);

            const patch: Partial<CampingSpot> = {};
            if (title && title.trim()) patch.name = title.trim();
            if (summary && summary.trim()) {
              patch.description = summary.trim();
              patch.descriptionAuto = true;
              patch.aiResult = {
                ...((currentSpot.aiResult || {}) as any),
                description: summary.trim(),
              } as any;
            }
            if (Object.keys(patch).length === 0) return;

            const updatedSpot: CampingSpot = { ...currentSpot, ...patch };
            setSpots(prev => prev.map(x => x.id === currentSpot.id ? updatedSpot : x));
            await upsertSpotsToFirebase([updatedSpot]);
          } catch (e) {
            console.warn('Could not auto-generate title/description for spot', e);
          } finally {
            generatingMetaIdsRef.current.delete(currentSpot.id);
            setGeneratingMetaIds(prev => {
              const next = new Set(prev);
              next.delete(currentSpot.id);
              return next;
            });
          }
        })();
      } else {
        generatingMetaIdsRef.current.delete(currentSpot.id);
      }
    }
  }, [setMapCenter, setMapZoom, setLayers, setSpots, reanalyzeSpot, setMapCommand, setGeneratingMetaIds, isPaidUser]);

  const handleSelectSpotDetails = useCallback(async (s: CampingSpot, options?: { openMobileSheet?: boolean }) => {
    if (!s || !s.location) return;
    setSelectedSpotId(s.id);
    setActiveHikingTrailId(null);

    if (typeof options?.openMobileSheet === 'boolean') {
      setShowMobileSheet(options.openMobileSheet);
    }

    if (!s.hikingTrails) {
      fetchNearbyHikingTrails(s.location).then(trails => {
        if (trails?.length > 0) {
          setSpots(prev => prev.map(x => x.id === s.id ? { ...x, hikingTrails: trails } : x));
        }
      });
    }

    if (!s.nearestCharger) {
      getNearestCharger(s.location, 10000).then(charger => {
        if (charger) {
          setSpots(prev => prev.map(x => x.id === s.id ? { ...x, nearestCharger: charger } : x));
        }
      });
    }

    if (!s.aiResult && s.spotType === 'algorithmic') {
      await reanalyzeSpot(s);
    }
  }, [setSpots, reanalyzeSpot]);

  return {
    selectedSpotId, setSelectedSpotId,
    activeHikingTrailId, setActiveHikingTrailId,
    showMobileSheet, setShowMobileSheet,
    handleSelectSpot, handleSelectSpotDetails, reanalyzeSpot,
    generatingMetaIds,
    removalFeedback, setRemovalFeedback
  };
};
